/**
 * High-level actions for chat operations.
 *
 * This module provides the primary API for chat operations. It wraps the lower-level
 * ChatDAO and ChatDraftState APIs, handling:
 * - Optimistic UI updates via draft state
 * - Error recovery (restore draft on 4xx errors)
 *
 */

import {completeOnboardingTask} from 'actions/OnboardingActions';
import OnboardingConstants, {NUEModule} from 'constants/OnboardingConstants';
import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {notifyChatListingRefresh} from 'llm/chat-library/ChatListingRefresh';
import notifications from 'notifications';
import * as ImportUtil from 'util/ImportUtil';
import * as net from 'util/Net';
import uniqueId from 'util/uniqueId';

import {trackMessageSent} from './ChatAnalytics';
import * as ChatDAO from './ChatDAO';
import {CreateChatOptions} from './ChatDAO';
import {
  AgentTypeError,
  ChatDraftMessage,
  ChatDraftState,
  ChatDraftStatus,
  getCreateDisplayMessageChatEvent,
  getDraftState,
  getOrCreateDraftState,
  removeFileFromDraft,
  replaceFileInDraft,
  setDraftState,
  CreateParams,
} from './ChatDraftState';
import {ChatFileUpload, ChatSelectedFile} from './ChatLibraryTypes';
import {handleInsufficientCredits} from './MeteringUtils';
import {getChatTypeForNewAgentChat} from './sidepanel/ChatTypeUtil';

// Re-export types for convenience
export {ChatDraftStatus};
export type {AgentTypeError, ChatDraftMessage, ChatDraftState, CreateParams};

interface ChatActionsTestOverrides {
  interruptChatSession?: (chatSessionId: string, chatEventId: string) => Promise<void>;
}

/** Test/story overrides for chat actions. Not for production use. */
export const _testOverrides: ChatActionsTestOverrides = {};

/**
 * Request that the agent stop responding to an in-progress user display message.
 */
export async function interruptChatSession(chatSessionId: string, chatEventId: string): Promise<void> {
  if (_testOverrides.interruptChatSession != null) {
    await _testOverrides.interruptChatSession(chatSessionId, chatEventId);
    return;
  }
  await ChatDAO.interruptChatSession(chatSessionId, chatEventId);
}

export async function createChat(
  chatType: ChatType,
  options: CreateChatOptions = {},
  forAgentType: AgentType,
  writeActionsEnabled: boolean = false
): Promise<{chatSessionId: string}> {
  let chatSessionId: string;
  try {
    chatSessionId = await ChatDAO.createChatSession(chatType, options, forAgentType, writeActionsEnabled);
  } catch (e) {
    handleInsufficientCredits(e, {chatSessionId: null, wasDraftStagedInState: false});
    throw e;
  }
  completeOnboardingTask(NUEModule.AI_AGENTS, OnboardingConstants.START_CHAT);
  return {chatSessionId};
}

export async function createRealtimeVoiceChat(
  forAgentType: AgentType
): Promise<{chatSessionId: string; chatType: ChatType}> {
  let chatSessionId: string;
  try {
    chatSessionId = await ChatDAO.createRealtimeVoiceChatSession(forAgentType);
  } catch (e) {
    handleInsufficientCredits(e, {chatSessionId: null, wasDraftStagedInState: false});
    throw e;
  }
  completeOnboardingTask(NUEModule.AI_AGENTS, OnboardingConstants.START_CHAT);
  return {chatSessionId, chatType: ChatType.UNIFIED};
}

/**
 * Set draft message state for a chat.
 *
 * Use this to track the user's in-progress message (for preserving across
 * navigation and showing draft indicators).
 *
 * @param chatSessionId - The chat session ID, or null for new chats
 * @param state - The draft state to set, or null to clear
 */
export function setDraftMessage(chatSessionId: string | null, state: ChatDraftState | null): void {
  setDraftState(chatSessionId, state);
}

export interface SubmitDraftMessageOptions {
  createParams?: CreateParams;
  analytics?: {messageCount: number};
  /**
   * If provided, use this draft directly instead of reading from global state.
   */
  draft?: ChatDraftState;
  /**
   * The ID of the last display message (user or assistant) at the time this message is being sent.
   * Used to detect when the optimistic message has been persisted.
   */
  replyToId?: string | null;
  /**
   * If this is a retry, the ID of the original user message being retried.
   * Used to filter out the original message from display when showing the retry.
   */
  retryOfId?: string | null;
}

/**
 * Submit a message for a chat session.
 *
 * By default, reads the draft from global state. If `options.draft` is provided,
 * uses that draft directly (and stores it in global state for optimistic UI).
 *
 * - Sends the message to the backend
 * - Sets draft to PENDING for optimistic UI rendering
 * - Registers error recovery to restore draft on 4xx errors
 *
 * This is fire-and-forget - errors are handled via notifications.
 *
 * @throws Error if no draft exists (neither provided nor in global state)
 */
export function submitDraftMessage(chatSessionId: string, options: SubmitDraftMessageOptions = {}): void {
  const {
    createParams = {},
    analytics = {messageCount: 0},
    draft: providedDraft,
    replyToId,
    retryOfId,
  } = options;
  const draft = providedDraft ?? getDraftState(chatSessionId);
  if (!draft) {
    throw new Error(`No draft found for chat session: ${chatSessionId}`);
  }

  const event = getCreateDisplayMessageChatEvent(chatSessionId, draft, createParams);

  trackMessageSent({
    chatSessionId,
    chatType: getChatTypeForNewAgentChat(event.agentType),
    agentType: event.agentType,
    messageCount: analytics.messageCount,
    fileCount: event.messageBlocks.filter((block) => block.type === 'file').length,
    includePublicData: event.sources?.some((s) => s.type === 'public_data') ?? false,
  });

  const messagePromise = ChatDAO.appendDisplayMessageToChatSession(event);

  // Set draft to PENDING for optimistic rendering, including replyToId and retryOfId
  // for tracking when the optimistic message has been persisted and for filtering retried messages.
  // Normalize to null so PENDING drafts always have consistent tracking data.
  setDraftState(chatSessionId, {
    ...draft,
    status: ChatDraftStatus.PENDING,
    failedUserMessage: undefined,
    replyToId: replyToId ?? null,
    retryOfId: retryOfId ?? null,
  });

  // On 4xx, move the message back into draft so the user can fix the error and try again.
  // On 5xx, show notification but keep message pending (server error, not user error).
  // Otherwise, use standard error handling by letting the error bubble up.
  messagePromise
    .then(() => {
      notifyChatListingRefresh();
    })
    .catch((e) => {
      if (!net.isServerRequestError(e)) {
        throw e;
      }

      const wasHandled = handleInsufficientCredits(e, {
        chatSessionId,
        wasDraftStagedInState: providedDraft == null,
      });
      if (wasHandled) {
        return;
      }

      // Only restore draft on 4xx errors (user can fix and retry)
      if (e.status >= 400 && e.status < 500) {
        const currentDraft = getDraftState(chatSessionId);
        if (currentDraft?.status === ChatDraftStatus.PENDING) {
          setDraftState(chatSessionId, {
            ...currentDraft,
            status: ChatDraftStatus.DRAFT,
          });
        }
      }

      // Show error notification for all server errors,
      // but don't re-throw - we've handled the error.
      notifications.notifyError(e);
    });
}

export interface CreateChatWithMessageOptions {
  createChatOptions?: CreateChatOptions;
  createParams?: CreateParams;
  /**
   * If provided, use this draft directly instead of reading from global state.
   * This avoids clobbering any user-authored draft when programmatically creating chats.
   */
  draft?: ChatDraftState;
}

/**
 * Create a new chat and submit a message.
 *
 * By default, reads the draft from the new-chat state (null key) and:
 * - Creates a new chat session
 * - Moves draft to the new chat session
 * - Submits the message
 *
 * If `options.draft` is provided, uses that draft directly without touching global state.
 *
 * @returns The created chat session ID
 * @throws Error if no draft exists (neither provided nor in global state)
 */
export async function createChatWithDraftMessage(
  chatType: ChatType,
  options: CreateChatWithMessageOptions = {}
): Promise<{chatSessionId: string}> {
  const {createParams = {}, draft: providedDraft, createChatOptions = {}} = options;

  const draft = providedDraft ?? getDraftState(null);
  if (!draft) {
    throw new Error('No draft found');
  }

  // Create the chat session, with the draft message
  let chatSessionId: string;
  try {
    chatSessionId = await ChatDAO.createChatSession(
      chatType,
      createChatOptions,
      draft.settings?.agentType,
      draft.settings?.newChatFullAccess
    );
  } catch (e) {
    handleInsufficientCredits(e, {
      chatSessionId: null,
      wasDraftStagedInState: providedDraft == null,
    });
    throw e;
  }
  completeOnboardingTask(NUEModule.AI_AGENTS, OnboardingConstants.START_CHAT);
  setDraftState(chatSessionId, draft);
  // Clear the new-chat draft if it was used
  if (!providedDraft) {
    setDraftState(null, null);
  }
  submitDraftMessage(chatSessionId, {createParams, draft});
  return {chatSessionId};
}

/**
 * Select files and start uploading them to S3.
 * Files are immediately added to the draft with completedUpload: null,
 * then updated with the upload data when complete, or removed on failure.
 *
 * Returns a promise that resolves when all uploads complete, or rejects
 * if any upload fails. Callers can ignore the promise for fire-and-forget behavior.
 */
export function selectFilesForDraft(chatSessionId: string | null, files: Array<File>): Promise<void> {
  const state = getOrCreateDraftState(chatSessionId);
  if (state.status !== ChatDraftStatus.DRAFT) {
    return Promise.resolve();
  }

  // Add files immediately, marked as loading (completedUpload: null)
  const newSelectedFiles: Array<ChatSelectedFile> = files.map((file) => ({
    key: uniqueId('file'),
    file,
    completedUpload: null,
  }));

  setDraftState(chatSessionId, {
    ...state,
    message: {
      ...state.message,
      selectedFiles: [...state.message.selectedFiles, ...newSelectedFiles],
    },
  });

  // Start uploading each file
  const uploadPromises = newSelectedFiles.map((selectedFile) =>
    uploadFileForDraft(chatSessionId, selectedFile)
  );

  return Promise.all(uploadPromises).then(() => undefined);
}

/**
 * Upload a single file and update the draft state when complete.
 */
async function uploadFileForDraft(
  chatSessionId: string | null,
  selectedFile: ChatSelectedFile
): Promise<void> {
  try {
    // Wrap in Promise.resolve() because ImportUtil.upload returns a jQuery Deferred
    const s3Location = await Promise.resolve(ImportUtil.upload(selectedFile.file));
    const completedUpload: ChatFileUpload = {
      s3Location: s3Location!,
      filename: selectedFile.file.name,
      mediaType: selectedFile.file.type,
    };
    replaceFileInDraft(chatSessionId, selectedFile.key, {...selectedFile, completedUpload});
  } catch (error) {
    // Remove the file if upload failed
    removeFileFromDraft(chatSessionId, selectedFile.key);
    throw error;
  }
}
